/**
 * The only path from document markdown (user- and AI-authored) to the DOM in this app.
 * `renderMarkdown` turns source into HTML with `marked`, then sanitises it with DOMPurify —
 * sanitising is the point, not an afterthought, since a document body can carry anything a
 * person typed or a generation produced. `headingOutline` reads the same source for the
 * editor's outline rail without rendering anything.
 *
 * DOMPurify needs a real DOM (`window.document.implementation.createHTMLDocument`) and only
 * has one in the browser this app actually ships to — there is no DOM under `bun test` (no
 * jsdom/happy-dom in this repo) and DOMPurify says so itself (`isSupported === false`) rather
 * than silently no-op'ing. `stripDangerousMarkup` below enforces the same hard bans (script,
 * style, iframe, object, embed, event-handler attributes, javascript:/data: URLs) at the
 * string level so `renderMarkdown` has one well-defined, testable behaviour either way. In
 * the browser DOMPurify still runs and is the sanitizer of record; the string pass is an
 * environment-independent floor under it, never a replacement for it.
 */
import { Marked, marked } from 'marked';
import DOMPurify from 'dompurify';

marked.setOptions({ gfm: true, breaks: false });

/**
 * Link and image targets are restricted to http(s), mailto and same-document/relative
 * references. `javascript:` and `data:` never reach the DOM — DOMPurify checks every href
 * and src against this before anything is kept.
 */
const SAFE_URI_PATTERN = /^(?:https?:|mailto:|#|\/|\.)/i;

let hooksInstalled = false;

function installHooks(): void {
  if (hooksInstalled || typeof DOMPurify.addHook !== 'function') return;
  hooksInstalled = true;
  // Belt-and-braces alongside ALLOWED_URI_REGEXP: strip any href/src that still reads as
  // javascript: or data: after whitespace/control-character tricks are collapsed.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    for (const attr of ['href', 'src']) {
      const value = node.getAttribute?.(attr);
      if (value && /^\s*(javascript|data):/i.test(value)) {
        node.removeAttribute(attr);
      }
    }
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      const href = node.getAttribute('href') ?? '';
      if (/^https?:\/\//i.test(href)) {
        node.setAttribute('rel', 'noopener noreferrer');
        node.setAttribute('target', '_blank');
      } else {
        node.removeAttribute('target');
      }
    }
  });
}

/** Drops <script>/<style>/<iframe>/<object>/<embed>, event-handler attributes and
 *  javascript:/data: link or image targets, at the string level — see the file header. */
function stripDangerousMarkup(html: string): string {
  let out = html.replace(/<(script|style|iframe|object|embed)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, '');
  out = out.replace(/<\/?(script|style|iframe|object|embed)\b[^>]*>/gi, '');
  out = out.replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '');
  out = out.replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '');
  out = out.replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '');
  // `style` and `formaction` go the same way as the event handlers. A document
  // body is human- and AI-authored and renders inside the app shell, so a
  // surviving `style="position:fixed;inset:0"` would let it paint itself over
  // the surrounding UI — a redress attack that needs no script at all.
  out = out.replace(/\s(?:style|formaction)\s*=\s*"[^"]*"/gi, '');
  out = out.replace(/\s(?:style|formaction)\s*=\s*'[^']*'/gi, '');
  out = out.replace(/\s(?:style|formaction)\s*=\s*[^\s>]+/gi, '');
  out = out.replace(/((?:href|src)\s*=\s*)"\s*(?:javascript|data):[^"]*"/gi, '$1"#"');
  out = out.replace(/((?:href|src)\s*=\s*)'\s*(?:javascript|data):[^']*'/gi, "$1'#'");
  return out;
}

/** Forces rel="noopener noreferrer" (and target="_blank") on external http(s) links. */
function hardenExternalLinks(html: string): string {
  return html.replace(/<a\s+([^>]*?)href\s*=\s*"(https?:\/\/[^"]*)"([^>]*)>/gi, (match, pre, href, post) => {
    const attrs = `${pre} ${post}`;
    const rel = /\brel\s*=/i.test(attrs) ? '' : ' rel="noopener noreferrer"';
    const target = /\btarget\s*=/i.test(attrs) ? '' : ' target="_blank"';
    return `<a ${pre}href="${href}"${post}${rel}${target}>`;
  });
}

/** Markdown source → sanitised HTML, safe to hand to `dangerouslySetInnerHTML`. */
export function renderMarkdown(src: string): string {
  return sanitize(marked.parse(src ?? '', { async: false }) as string);
}

/**
 * #374: a long-text FIELD value (description, idea body, notes) in view mode. Stricter than a
 * document body: raw HTML in the value is dropped at the token level — block or inline, it is
 * neither rendered nor shown as literal text — so only Markdown's own constructs reach the
 * sanitiser. The stored value is untouched; this is a read-only projection of it.
 */
const fieldMarked = new Marked({ gfm: true, breaks: true, renderer: { html: () => '' } });

export function renderFieldMarkdown(src: string): string {
  return sanitize(fieldMarked.parse(src ?? '', { async: false }) as string);
}

function sanitize(html: string): string {
  installHooks();
  const floor = hardenExternalLinks(stripDangerousMarkup(html));

  if (typeof DOMPurify.sanitize !== 'function' || DOMPurify.isSupported === false) {
    return floor;
  }

  return DOMPurify.sanitize(floor, {
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
    // `style` is forbidden as an ATTRIBUTE as well as a tag. Document bodies
    // here are human- and AI-authored and render inside the app shell, so a
    // surviving `style="position:fixed"` would let a document paint itself
    // over the surrounding UI — a redress attack that needs no script at all.
    // `formaction` is banned for the same reason a `javascript:` href is.
    FORBID_ATTR: ['srcdoc', 'style', 'formaction'],
    ALLOWED_URI_REGEXP: SAFE_URI_PATTERN,
    ADD_ATTR: ['target', 'rel'],
  });
}

export interface HeadingOutlineEntry {
  level: number;
  text: string;
  slug: string;
}

function slugify(text: string, seen: Map<string, number>): string {
  const base =
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'section';
  const count = seen.get(base) ?? 0;
  seen.set(base, count + 1);
  return count === 0 ? base : `${base}-${count}`;
}

/** The document's heading outline, in document order, for the editor's outline rail. */
export function headingOutline(src: string): HeadingOutlineEntry[] {
  const tokens = marked.lexer(src ?? '');
  const seen = new Map<string, number>();
  const outline: HeadingOutlineEntry[] = [];
  for (const token of tokens) {
    if (token.type === 'heading') {
      outline.push({ level: token.depth, text: token.text, slug: slugify(token.text, seen) });
    }
  }
  return outline;
}
